import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { deriveCapabilities, CAP } from '../lib/planAccess';
import { useEsTelefono } from '../lib/esTelefono';
import { toast } from 'sonner';
import {
    Activity, CheckCircle2, Send, Zap,
    Loader2, ChevronLeft,
} from 'lucide-react';
import { Estrellas, BarraDeTramos, Pildora } from '../components/reports/piezas';
// La pregunta de la noche abre EL MISMO bloque de extras del día (punto 32), no una copia.
import ExtrasDelDia from '../components/nutrition/ExtrasDelDia';
import CampoDePeso from '../components/CampoDePeso';
// «Tu peso del día», el mismo del mensual (28-10).
import { MensualTuPeso } from '../components/reports/MensualTuMes';
// Las fotos y las medidas, las mismas del reporte (02-11).
import TresFotos from '../components/reports/TresFotos';
import { TusMedidas } from '../components/reports/MensualPaso3';
import { MEDIDAS, revisarMedidas } from '../lib/medidas';
import usePuntos from '../hooks/usePuntos';
import useBorradorEnElServidor from '../hooks/useBorradorEnElServidor';
import { mensajeDeError } from '../lib/mensajeDeError';
import { anotar } from '../lib/rastro';
// Los botones del entreno, el cardio y los suplementos, y cómo se nombra cada respuesta:
// los mismos que lee la ficha del entrenador (Jesús, 20-10).
import {
    ENTRENO_OPCIONES, CARDIO_OPCIONES, SUPLES_OPCIONES, ACTIVIDAD_OPCIONES, HORAS_SUENO_OPCIONES,
    RENDIMIENTO_OPCIONES, SENSACIONES_ENTRENO_OPCIONES, CANTIDAD_COMIDA_OPCIONES, ENTRENO_HECHO,
    entrenoDeHoy,
} from '../lib/respuestasDelCierre';
// «Tu dieta hoy» del check-in, leída del mismo juez que Mi día (26-10).
import { DietaPlanificada, rutaDeLaComida } from '../components/inicio/NutricionHoy';
import { useDiaDeHoy } from '../hooks/useDiaDeHoy';
import { objetivoDelDia } from '../lib/estadoMacros';
import { ahoraDeLaApp } from '../lib/horaEspana';

const inputCls = "w-full bg-muted border border-input rounded-xl px-3 py-2.5 text-foreground text-sm placeholder-white/20 focus:outline-none focus:border-[#FF671F] transition-colors";

// Aquí había una tarjeta con la etiqueta de riesgo del cliente ("Saludable" / "Atención" /
// "En riesgo") y el motivo debajo. Se quitó el 07-08 (punto 6 del documento de Jesús): esa
// etiqueta es una nota de gestión del entrenador, para saber a quién hay que llamar, y sus
// motivos hablan de cobros y de bajas. El cliente no tiene por qué verse etiquetado en su
// propio panel. Vive solo en el lado del entrenador, y su ruta también.
//
// Y aquí vivían las cinco caritas del ánimo (`MOOD_FACES`). No las pintaba nadie desde el
// 31-07 y las sensaciones del día se preguntan ahora con estrellas (punto 01 del doc
// 24-08), así que se van con el resto del check-in viejo.

// El día del RELOJ DEL CLIENTE (bloque F, 23-08). `toISOString()` era el día UTC: desde
// América por la tarde «hoy» ya era mañana y el cierre de hoy nunca contaba como de hoy.
const todayKey = () => ahoraDeLaApp().toLocaleDateString('en-CA');
const isSameDay = (iso) => iso && new Date(iso).toLocaleDateString('en-CA') === todayKey();

// «miércoles», en minúscula y con el reloj del cliente. Sirve para que el cierre de un día
// que no es hoy hable de ESE día («¿Cómo fue el miércoles?») y no de «hoy» (Gonzalo, 4-09,
// punto 76: cerraba el miércoles y todo le hablaba de hoy).
const nombreDelDia = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long' });

// La línea del día, su detalle, los textos libres y cómo se nombra cada respuesta viven en
// `lib/detalleDelCierre.js` desde el 21-10: los lee también la ficha del entrenador
// (`components/CoachCheckins.jsx`), que abre cada día igual que «Tus días».

// ── Subcomponentes a nivel de módulo (mantienen el foco al teclear) ──────────
const Card = ({ className = '', children }) => (
    <div className={`bg-card border border-border rounded-2xl ${className}`}>{children}</div>
);

// AQUÍ VIVÍA `EntradaDelHistorial`, la ficha de una entrada de «Reportes anteriores»
// (semanal o mensual): la píldora del tipo, la fecha, el peso y los porcentajes, y el
// comentario del entrenador. Ese bloque lo quitó Jesús el 6-09-2026 y no queda nada que
// lo pinte, así que se va con él -- y con él `fechaDeEntrada` y `TIPO_CHECKIN`, que no
// usaba nadie más. Lo guardado no se toca: el backend sigue devolviendo esas entradas y
// siguen contando para «Cargar más»; lo que desaparece es la lista.

// Aquí vivían `BoolPicker` (los dos botones de sí/no del check-in viejo) y `Collapsible`,
// la caja plegable del semanal y del mensual. Los dos formularios se cayeron con T11 del
// doc 16-08 y desde el 24-08 no queda nada que los pinte.

// AQUÍ VIVÍAN «TUS DÍAS» Y «MI DIARIO» (la lista de días con sus huecos «Sin rellenar», y el
// Diario con sus filtros). Se fueron el 28-10 (Enrique): los check-in pasados se ven en
// Seguimiento -> «Histórico de check-ins» (`components/HistoricoDeCheckIns`), que es a
// donde se llega también al enviar el de hoy.


// ── El cierre del día (T4 del doc 16-08, rehecho con el doc 24-08) ───────────
//
// ONCE PREGUNTAS, una por tarjeta, y una sola encendida cada vez. El orden lo manda el
// doc del 24: sensaciones, entreno (y cómo fue), cardio, movimiento, suplementos, extras,
// descanso, energía, hambre, notas y peso.
//
// Lo condicional lo sigue decidiendo el servidor (`GET /checkins/hoy`): la pantalla pinta
// lo que le digan y no vuelve a calcular por su cuenta si le tocan suplementos o qué
// comidas se dejó sin registrar.

// Aquí vivía `Escala`, los cinco botones numerados con sus extremos. El check-in del 26-10
// pregunta la energía y el hambre con estrellas, como el descanso.

// Los grupos de botones. En una columna, para las respuestas que son una frase (el
// check-in del 26-10): en fila no caben en el móvil, y alineadas a la izquierda se leen.
const COLUMNAS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' };
// LA MARCADA, EN NARANJA LLENO (artefacto de Enrique, 26-10), y las cortas en píldoras
// (`pildoras`: el cardio y los extras), las mismas del quincenal (`piezas.Pildora`).
const Opciones = ({ opciones, value, onChange, testId, columnas = 3, pildoras = false }) => (pildoras ? (
    <div className="flex flex-wrap gap-2">
        {opciones.map(o => (
            <Pildora key={o.v} activo={value === o.v} onClick={() => onChange(o.v)}
                testid={`${testId}-${o.v}`}>{o.l}</Pildora>
        ))}
    </div>
) : (
    <div className={`grid gap-2 ${COLUMNAS[columnas] || COLUMNAS[3]}`}>
        {opciones.map(o => (
            <button key={o.v} type="button" onClick={() => onChange(o.v)}
                data-testid={`${testId}-${o.v}`}
                className={`border font-semibold text-[13px] transition-all py-2.5 rounded-lg ${
                    columnas === 1 ? 'px-3 text-left leading-snug' : 'px-2'} ${value === o.v
                    ? 'border-brand bg-brand text-black'
                    : 'border-border bg-transparent text-foreground/70 hover:border-white/30'}`}>
                {o.l}
            </button>
        ))}
    </div>
));

// ── LA REGLA DEL COLOR (puntos 13, 14 y 18 del doc 24-08) ────────────────────
//
// «La tarjeta naranja significa "esto es lo que toca ahora". Sólo una encendida a la vez;
// en cuanto se contesta, se apaga y se enciende la siguiente.» Y al contestar, DOS
// señales y no una: «el tono dice por dónde vas; el tick dice lo que ya está hecho».
//
// Con eso no hace falta ni barra de progreso ni contador: el color dice por dónde va.
// Y al reabrir el cierre para corregirlo, todo sale contestado y en oscuro; tocando una
// respuesta se vuelve a encender esa tarjeta (`onAbrir`).
//
// `cola` es lo que cuelga de una respuesta y sigue haciendo falta con la tarjeta ya
// apagada: el «¿cuál y por qué?» de los suplementos y el campo de los extras. Se pinta
// abajo, encendida o no; si se escondiera al apagarse, contestar «No todos» haría
// desaparecer la caja donde tenía que escribir cuál.
const Tarjeta = ({ titulo, ayuda, ayudaCursiva = false, encendida, contestada,
                  resumen, onAbrir, cola, testId, children }) => (
    // Y SIN EL NARANJA, que era del acordeón. Marcaba «esta es la que toca» cuando sólo
    // había una abierta; con las ocho a la vista no distingue nada, y en esta app el
    // naranja quiere decir «algo que corregir» (la regla del punto 76). Ocho tarjetas
    // naranjas se leen como ocho errores. Lo que dice si está contestada es el tic, y lo
    // que dice qué falta es el pie.
    <div data-testid={testId} data-encendida={encendida ? '1' : '0'}
        className="rounded-2xl border border-border bg-card p-4 transition-colors">
        {/* TODAS ABIERTAS, SIN PLEGAR NADA («El día», 31-08). `encendida` llega siempre a
            true desde que la pantalla dejó de ser un acordeón; la rama de abajo -- la
            tarjeta cerrada con su resumen -- se queda escrita porque el mismo componente
            lo usan el semanal y el mensual, que sí pliegan. Si un día vuelve el acordeón
            del cierre, está aquí. */}
        {encendida ? (
            <div className="flex items-start gap-2">
                {contestada && (
                    <CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0 mt-0.5"
                        data-testid={`${testId}-tick`} />
                )}
                <div className="min-w-0 flex-1">
                    <span className="text-sm block text-foreground font-semibold">{titulo}</span>
                    {ayuda && (
                        <p className={`text-[11px] text-foreground/40 mt-0.5 ${ayudaCursiva ? 'italic' : ''}`}>
                            {ayuda}
                        </p>
                    )}
                    <div className="mt-2">{children}</div>
                </div>
            </div>
        ) : (
            // Apagada, y entera pulsable: «al tocar una respuesta, vuelve a encenderse esa
            // tarjeta» (punto 18). Con la que aún no ha contestado vale igual, para poder
            // adelantarse sin tener que contestar por orden.
            <button type="button" onClick={onAbrir} data-testid={`${testId}-abrir`}
                className="w-full flex items-start gap-2 text-left">
                {contestada && (
                    <CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0 mt-0.5"
                        data-testid={`${testId}-tick`} />
                )}
                <div className="min-w-0 flex-1">
                    <span className="text-sm block text-foreground/50">{titulo}</span>
                    {/* «Con su respuesta marcada debajo.» */}
                    {contestada && (
                        <span className="text-sm text-foreground/80 block mt-0.5"
                            data-testid={`${testId}-resumen`}>{resumen}</span>
                    )}
                </div>
            </button>
        )}
        {cola && <div className="mt-2">{cola}</div>}
    </div>
);

// Aquí vivía `diasParaPesarse`, los días entre los que se puede fechar un pesaje. Se fue
// con la casilla del peso a `components/CampoDePeso.jsx` (bloque 4 del 1-09), entera y con
// su regla: el número lo sigue diciendo el servidor -- fallo 7 del repaso del 24-08, tres
// sitios y tres números para la misma decisión --, ahora en `GET /reports/evolution`.

// Los valores viejos de `entreno_respuesta` se leen con la pregunta nueva
// (`lib/respuestasDelCierre.entrenoDeHoy`): sin eso, al reabrir un cierre de ayer la
// pregunta salía en blanco.

// Los textos de la ayuda, más pequeños que la pregunta.
const Ayuda = ({ children }) => (
    <p className="text-[11px] text-foreground/40 mt-0.5 leading-snug">{children}</p>
);

// Aquí vivía `Bloque`, el rótulo de cada bloque del paso. Desde el 28-10 cada sección es una
// página y su título va en la cabecera.

// Las estrellas con lo que quiere decir el 1 y el 5 debajo, en dos renglones: las frases
// del artefacto son largas y una al lado de la otra no caben en el móvil. Las cortas (el
// hambre) van en una línea con los extremos de la propia `Estrellas` (artefacto 26-10).
const EstrellasConExtremos = ({ valor, onChange, testid, uno, cinco }) => (
    <div>
        <Estrellas testid={testid} valor={valor} onChange={onChange} />
        {(uno || cinco) && (
            <div className="mt-1 space-y-0.5">
                {uno && <p className="text-[11px] text-foreground/40">1 · {uno}</p>}
                {cinco && <p className="text-[11px] text-foreground/40">5 · {cinco}</p>}
            </div>
        )}
    </div>
);

// EL CHECK-IN DIARIO, UNA PÁGINA POR SECCIÓN (Enrique, 28-10), como el mensual: arriba
// «‹ Atrás», el título de la sección en naranja y un tramo de barra por sección.
//
//   1 · Tu peso del día              «Registrar peso» lo guarda en su serie y pasa a la 2.
//                                    OBLIGATORIO si hoy no se ha pesado (Enrique, 28-10); si ya
//                                    se pesó, está hecha, se empieza en la 2 y lleva «Continuar»
//   2 · Sueño y descanso             pasa sola con las dos contestadas
//   3 · Tu dieta hoy                 la «Dieta planificada» de Mi día y «Continuar» (sin macros no sale)
//   4 · Entrenamiento de fuerza      si tiene rutina; pasa sola («descanso» y «no fui», al tocarlas;
//                                    las otras tres, con rendimiento y sensaciones)
//   5 · Energía, cardio y actividad  la energía; pasa sola
//   6 · Cardio y actividad diaria    pasa sola con las dos
//   7 · Acerca de la dieta           pasa sola con las tres (con «Sí» en los extras, «Continuar»:
//                                    antes tiene que apuntarlos)
//   8 · Suplementos                  pasa sola (con «No toda», «Continuar»: el «¿Cuál y por qué?»)
//   9 · Tus fotos                    OPCIONAL (Enrique, 02-11): «Ahora no», o «Continuar» con las
//                                    tres de hoy subidas. Se suben solas, como en el reporte
//                                    (`TresFotos`, POST /reports/photos)
//  10 · Tus medidas                  OPCIONAL (Enrique, 02-11): las diez del mensual con su vídeo;
//                                    «Ahora no», o «Continuar» con las diez, que las guarda como
//                                    toma suelta (POST /clients/me/medidas). El informe del mes
//                                    las compara como cualquier otra toma (`core/puntos._tomas_de`)
//  11 · Notas y cierre               las notas, opcionales, y «Enviar»
//
// Las fotos y las medidas no viajan con el check-in ni van al borrador: se guardan en su
// sitio al subirlas o al dar a «Continuar», se envíe o no el check-in.
//
// CADA RESPUESTA SE GUARDA SOLA (26-10 y 28-10): al rato de tocarla va al borrador del
// servidor, y al pasar de sección, ya. Si sale de la pantalla o cierra la sesión, lo
// contestado está guardado, y lo que no envíe se envía solo al acabar su día con lo que haya
// (`pasada_de_borradores`). El peso no viaja con el check-in: se guarda en su serie al
// registrarlo (`CampoDePeso`), llegue o no a la última sección. Al volver, se sigue por la
// primera sección que le falte.
//
// AL VOLVER ATRÁS, COMO EL CUESTIONARIO DEL ALTA (Enrique, 28-10): la sección sale con lo
// que marcó y sin «Continuar»; tocar esa misma respuesta, u otra, la deja entera y pasa.
// Por eso aquí tocar la estrella marcada no la desmarca (`estrella`): la confirma.
//
// «Sensaciones generales del día» ya no está: el artefacto no la trae (decisión de
// Francisco, 26-10). Lo que contestó en días de antes se conserva al reeditar
// (`routes/checkins._LO_QUE_PREGUNTA_EL_CIERRE`).
//
// `inicial` es el check-in ya enviado hoy cuando se entra a EDITARLO (P75, doc 23-08): el
// formulario arranca con lo que puso y al enviar SUSTITUYE al de antes. `borrador`, lo que
// guardó a medias. Sin ninguno de los dos, en blanco.
const CierreDelDia = ({ api, hoy, dia, onGuardado, onPeso, abrirPeso = false, inicial = null }) => {
    const navigate = useNavigate();
    const { habilitaciones, profile, user, token } = useAuth();
    // LA DIETA DE HOY, LA MISMA «DIETA PLANIFICADA» DE INICIO (Francisco, 28-10; antes, el
    // «sí o no» del audio de Enrique del 26-10): las comidas con su estado, con la misma
    // cuenta (`useDiaDeHoy`) y el mismo día de la dieta (`GET /diets/{fecha}`).
    const objetivo = objetivoDelDia(dia?.objetivo_comidas || null);
    const diaDeHoy = useDiaDeHoy({
        api, userId: user?.id, fecha: hoy?.fecha, dieta: dia, objetivo, activo: !!hoy?.fecha && !!objetivo,
    });
    // ¿SU PLAN LE DA RUTINA? (Trello de César, 24-09). Es la cuenta de CAP.RUTINA sin el
    // interruptor de la pantalla, igual que en Mi semana. Sin rutina no hay sección de entreno.
    const conEntreno = !habilitaciones || deriveCapabilities(habilitaciones, {
        rutinaVisible: true, rutinaComprada: !!profile?.rutina_comprada })[CAP.RUTINA];
    const [enviando, setEnviando] = useState(false);
    const [aviso, setAviso] = useState([]);
    const [f, setF] = useState(() => {
        const de = inicial || hoy?.borrador || {};
        return {
            horas_sueno: de.horas_sueno ?? null,
            descanso: de.descanso ?? null,
            entreno_respuesta: de.entreno_respuesta ? entrenoDeHoy(de.entreno_respuesta) : null,
            rendimiento: de.rendimiento ?? null,
            sensaciones_entreno: de.sensaciones_entreno ?? null,
            energy: de.energy ?? null,
            cardio: de.cardio ?? null,
            actividad: de.actividad ?? null,
            cantidad_comida: de.cantidad_comida ?? null,
            hunger_anxiety: de.hunger_anxiety ?? null,
            extras_respuesta: de.extras_respuesta ?? null,
            suplementos: de.suplementos?.respuesta ?? null,
            suplementos_detalle: de.suplementos?.detalle || '',
            notas: de.notas?.texto || '',
            // Sin la casilla de «Compartir» (el artefacto no la trae): lo que ya estuviera
            // compartido se queda compartido al reeditar, y lo nuevo es solo para su diario.
            compartida: de.notas?.compartida ?? false,
        };
    });
    // `tocado`: la última respuesta la ha puesto él ahora, y no al abrir. Solo entonces una
    // sección que pasa sola pasa.
    const tocado = useRef(false);
    const set = (campo, valor) => { tocado.current = true; setF(prev => ({ ...prev, [campo]: valor })); setAviso([]); };
    // Las estrellas devuelven null al tocar la marcada: aquí eso es confirmarla (ver arriba).
    const estrella = (campo) => (v) => set(campo, v == null ? f[campo] : v);

    // El día que se rellena: el que dice el servidor y, si no llegó, el del reloj del cliente.
    // El extra que se apunte desde aquí va a la lista de ESE día.
    const fechaDelDia = hoy?.fecha || todayKey();
    // ¿Ya se pesó hoy? Entonces la sección del peso está hecha (y su casilla sale con él).
    const pesadoHoy = String(hoy?.ultimo_peso?.fecha || '').slice(0, 10) === fechaDelDia;

    // Los extras del día: EL MISMO bloque que el de Inicio (ficha 03 del 5-09), con
    // `origen="checkin"` para saber cuánto se apunta desde aquí.
    const [extras, setExtras] = useState(() => dia?.extras || []);

    // ── Las secciones ──────────────────────────────────────────────────────────
    // `titulo` de cada pregunta es lo que sale en «Te queda por contestar» si se deja en
    // blanco. `sola`: pasa a la siguiente en cuanto está entera, sin «Continuar».
    const pregunta = (id, titulo, hecha) => ({ id, titulo, hecha });
    const seccionesCon = (v) => {
        const entrenoHecho = ENTRENO_HECHO.includes(v.entreno_respuesta);
        return [
            // PESARSE ES OBLIGATORIO SI HOY NO SE HA PESADO (Enrique, 28-10): sin el peso de
            // hoy la sección no está entera, y no se pasa ni se envía.
            { clave: 'peso', titulo: 'Tu peso del día', preguntas: [pregunta('peso', 'Tu peso del día', pesadoHoy)] },
            {
                clave: 'sueno', titulo: 'Sueño y descanso', sola: true,
                preguntas: [
                    pregunta('horas_sueno', '¿Cuántas horas dormiste anoche?', v.horas_sueno != null),
                    pregunta('descanso', '¿Fue un descanso de calidad?', v.descanso != null),
                ],
            },
            // Sin macros no sale, como en Inicio: no hay con qué decir si está lista.
            ...(objetivo ? [{ clave: 'dieta', titulo: 'Tu dieta hoy', preguntas: [] }] : []),
            ...(conEntreno ? [{
                clave: 'entreno', titulo: 'Entrenamiento de fuerza', sola: true,
                preguntas: [
                    pregunta('entreno', '¿Hiciste el entreno que tocaba como tocaba?', v.entreno_respuesta != null),
                    ...(entrenoHecho ? [
                        pregunta('rendimiento', '¿Y qué tal fue a nivel rendimiento?', v.rendimiento != null),
                        pregunta('sensaciones_entreno', '¿Buenas sensaciones entrenando?', v.sensaciones_entreno != null),
                    ] : []),
                ],
            }] : []),
            {
                clave: 'energia', titulo: 'Energía, cardio y actividad', sola: true,
                preguntas: [pregunta('energy', 'Niveles de energía durante el día', v.energy != null)],
            },
            {
                clave: 'cardio', titulo: 'Cardio y actividad diaria', sola: true,
                preguntas: [
                    pregunta('cardio', '¿Cumpliste el cardio pautado?', v.cardio != null),
                    pregunta('actividad', '¿Fuiste lo suficientemente activo?', v.actividad != null),
                ],
            },
            {
                // Con «Sí» en los extras tiene que apuntarlos: ahí no pasa sola.
                clave: 'acerca_dieta', titulo: 'Acerca de la dieta', sola: v.extras_respuesta !== 'si',
                preguntas: [
                    pregunta('cantidad_comida', '¿Qué tal con la cantidad de comida?', v.cantidad_comida != null),
                    pregunta('hambre', 'Niveles de hambre y ansiedad durante el día', v.hunger_anxiety != null),
                    pregunta('extras', '¿Comiste algo más fuera de la dieta?', v.extras_respuesta != null),
                ],
            },
            {
                // Con «No toda», el «¿Cuál y por qué?»: tampoco pasa sola.
                clave: 'suplementos', titulo: 'Suplementos', sola: v.suplementos !== 'no_todos',
                preguntas: [pregunta('suplementos', '¿Tomaste la suplementación que tocaba?', v.suplementos != null)],
            },
            // Opcionales: sin preguntas, así que nunca frenan el envío.
            { clave: 'fotos', titulo: 'Tus fotos', preguntas: [] },
            { clave: 'medidas', titulo: 'Tus medidas corporales', preguntas: [] },
            { clave: 'notas', titulo: 'Notas y cierre', preguntas: [] },
        ];
    };
    const SECCIONES = seccionesCon(f);
    const entera = (s) => s.preguntas.every(p => p.hecha);
    const todas = SECCIONES.flatMap(s => s.preguntas);

    // POR DÓNDE SE EMPIEZA: al reabrir uno enviado, por el principio; si no, por la primera
    // sección que le falte: el peso si hoy no se ha pesado, y si no, la siguiente sin acabar.
    const [clave, setClave] = useState(() => {
        // Y si viene a pesarse («Hoy toca pesarte», `?abrir=peso`), por el peso.
        if (inicial || abrirPeso) return 'peso';
        const conPreguntas = seccionesCon(f).filter(s => s.preguntas.length);
        const falta = conPreguntas.find(s => !s.preguntas.every(p => p.hecha));
        return falta ? falta.clave : 'notas';
    });
    const indice = Math.max(0, SECCIONES.findIndex(s => s.clave === clave));
    const seccion = SECCIONES[indice];
    const ultima = indice === SECCIONES.length - 1;

    // EL FRENO QUEDA EN EL RASTRO (25-10): «no me deja enviar el check-in» se tiene que poder
    // ver por su correo. Y se dice qué falta: un botón que no avanza sin decir por qué es peor.
    const frenar = (faltan, mensaje) => {
        setAviso(faltan);
        anotar({ tipo: 'freno', mensaje, detalle: { seccion: seccion.titulo, faltan } });
    };

    // Lo que viaja: igual en el borrador y en el envío.
    const respuestas = () => {
        const entrenoHecho = ENTRENO_HECHO.includes(f.entreno_respuesta);
        return {
            type: 'daily',
            fecha: fechaDelDia,
            // El día que vive el cliente y la hora de su reloj (bloque F, 23-08, y 15-09): el
            // servidor solo acepta el check-in de hoy, y hoy es el suyo.
            hoy: todayKey(),
            hora_cliente: ahoraDeLaApp().getHours(),
            horas_sueno: f.horas_sueno,
            descanso: f.descanso,
            entreno_respuesta: conEntreno ? f.entreno_respuesta : null,
            // Lo que cuelga del entreno, solo si entrenó: cambiar de idea no deja restos.
            rendimiento: conEntreno && entrenoHecho ? f.rendimiento : null,
            sensaciones_entreno: conEntreno && entrenoHecho ? f.sensaciones_entreno : null,
            energy: f.energy,
            cardio: f.cardio,
            // `movimiento` y las estrellas del entreno ya no se preguntan: las escribe el servidor
            // a partir de `actividad` y `rendimiento` (`core/respuestas_del_cierre`).
            actividad: f.actividad,
            cantidad_comida: f.cantidad_comida,
            hunger_anxiety: f.hunger_anxiety,
            // La respuesta se guarda aquí; lo que escribió ya está en la lista de extras del día.
            extras_respuesta: f.extras_respuesta,
            suplementos: f.suplementos
                ? { respuesta: f.suplementos, detalle: f.suplementos_detalle.trim() || null }
                : null,
            notas: f.notas.trim() ? { texto: f.notas.trim(), compartida: f.compartida } : null,
            // LO QUE YA NO SE PREGUNTA, NO SE BORRA: el servidor conserva lo que esta pantalla no
            // manda (`_LO_QUE_PONE_EL_SERVIDOR`). Estas cuatro siguen viajando como estaban.
            cena_hecha: inicial?.cena_hecha ?? null,
            comida_pendiente: inicial?.comida_pendiente ?? null,
            exceso_nota: inicial?.exceso_nota || null,
            entreno_nota: inicial?.entreno_nota || null,
        };
    };

    // CUALQUIER RESPUESTA SE GUARDA SOLA (Francisco, 26-10): cada cambio va al borrador al
    // rato, y lo pendiente se manda también al salir de la pantalla o cerrar la pestaña
    // (`hooks/useBorradorEnElServidor`). Editando un día ya enviado no hay borrador
    // (`inicial`): eso se corrige con «Enviar».
    const borrador = useBorradorEnElServidor(api, '/checkins/borrador', respuestas);
    const primeraVez = useRef(true);
    useEffect(() => {
        // Al abrir, el formulario ya trae lo guardado: no hay nada nuevo que mandar.
        if (primeraVez.current) { primeraVez.current = false; return; }
        if (!inicial) borrador.programar();
    }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

    // Y AL PASAR DE SECCIÓN, YA (28-10): lo de la sección que deja queda guardado aunque cierre
    // la sesión justo después. Sin red se sigue igual: el siguiente cambio lo vuelve a mandar.
    const guardarYa = () => {
        if (inicial) return;
        borrador.guardarYa().catch(err =>
            console.error('No se pudo guardar el check-in a medias:', err?.response?.data || err));
    };

    const irA = (i) => {
        const destino = SECCIONES[Math.min(Math.max(i, 0), SECCIONES.length - 1)];
        guardarYa();
        tocado.current = false;
        setClave(destino.clave);
        setAviso([]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // LAS QUE PASAN SOLAS: en cuanto la respuesta que acaba de tocar deja la sección entera,
    // a la siguiente, tras un instante para que se vea la marcada (como en el mensual).
    useEffect(() => {
        if (!tocado.current || !seccion.sola || !entera(seccion) || ultima) return undefined;
        const t = setTimeout(() => irA(indice + 1), 250);
        return () => clearTimeout(t);
    }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

    const continuar = () => {
        const faltan = seccion.preguntas.filter(p => !p.hecha).map(p => p.titulo);
        if (faltan.length) return frenar(faltan, 'Check-in: sección sin terminar');
        irA(indice + 1);
    };

    // ── Fotos y medidas (02-11), opcionales ─────────────────────────────────────
    // Cuántas de las tres fotos lleva subidas hoy: con las tres se enciende «Continuar».
    const [fotosHoy, setFotosHoy] = useState(0);
    // Las tomas de medidas (las de los reportes, las sueltas y las del alta), para poner al
    // lado de cada casilla la de la última vez, como en el mensual. Se piden al llegar a las
    // fotos o a las medidas, no al abrir el check-in.
    const [conTomas, setConTomas] = useState(false);
    useEffect(() => { if (['fotos', 'medidas'].includes(clave)) setConTomas(true); }, [clave]);
    const puntos = usePuntos(api, { activo: conTomas });
    const tomas = (puntos.datos?.tomas_medidas || []).filter(t => t?.fecha && t.measurements);
    const tomaDeHoy = tomas.filter(t => t.fecha === fechaDelDia && t.origen === 'suelta').slice(-1)[0] || null;
    const tomaAnterior = tomas.filter(t => t.fecha < fechaDelDia).slice(-1)[0] || null;
    const [medidas, setMedidas] = useState({});
    // Si hoy ya las apuntó (desde aquí o desde Seguimiento), salen puestas: dar a «Continuar»
    // otra vez corrige la toma de hoy, no crea otra (una toma suelta por día).
    const prellenadas = useRef(false);
    useEffect(() => {
        if (prellenadas.current || !tomaDeHoy) return;
        prellenadas.current = true;
        setMedidas(prev => (Object.keys(prev).length ? prev : Object.fromEntries(
            Object.entries(tomaDeHoy.measurements).map(([k, v]) => [k, String(v)]))));
    }, [tomaDeHoy]);
    const medidasEnteras = MEDIDAS.every(({ key }) => String(medidas[key] ?? '').trim() !== '');
    const [guardandoMedidas, setGuardandoMedidas] = useState(false);
    const guardarMedidas = async () => {
        // La misma regla que el mensual y que el servidor: las diez, y en su rango.
        const chequeo = revisarMedidas(medidas);
        if (!chequeo.ok) { toast.error(chequeo.error); return; }
        setGuardandoMedidas(true);
        try {
            await api.post('/clients/me/medidas', { medidas });
            toast.success('Medidas guardadas');
            puntos.recargar();
            irA(indice + 1);
        } catch (err) {
            toast.error(mensajeDeError(err, 'No hemos podido guardar tus medidas'));
        } finally {
            setGuardandoMedidas(false);
        }
    };

    const enviar = async () => {
        // Todo lo de todas las secciones: se puede haber vuelto atrás y borrado algo. Se le
        // lleva a la primera que tenga algo en blanco, con lo que falta dicho.
        const falta = SECCIONES.find(s => !entera(s));
        if (falta) {
            irA(SECCIONES.indexOf(falta));
            return frenar(falta.preguntas.filter(p => !p.hecha).map(p => p.titulo), 'Cierre del día sin terminar');
        }
        setEnviando(true);
        // Lo que iba al borrador ya va en el envío: que no llegue detrás y cree otro.
        borrador.cancelar();
        try {
            await api.post('/checkins', respuestas());
            toast.success('Anotado. Mañana seguimos.');
            onGuardado(fechaDelDia);
        } catch (err) {
            console.error('No se pudo guardar el cierre del día:', err?.response?.data || err);
            // EL MOTIVO DEL SERVIDOR, SI LO DA («Solo puedes rellenar el check-in de hoy»):
            // esconderlo le deja repitiendo algo que no va a funcionar. Lo escrito no se pierde.
            toast.error(mensajeDeError(err, 'No hemos podido guardar tu día. Inténtalo en un momento.'));
            // Y lo contestado va al borrador, que el envío no llegó (si sale sin reintentar, se
            // envía solo al acabar el día).
            guardarYa();
        } finally {
            setEnviando(false);
        }
    };

    const tarjeta = (id, titulo, ayuda, hecha, campo, cola = null) => (
        <Tarjeta testId={`cierre-${id}`} titulo={titulo} ayuda={ayuda} encendida contestada={hecha} cola={cola}>
            {campo}
        </Tarjeta>
    );

    const boton = 'w-full rounded-xl py-3 text-sm font-bold uppercase tracking-wider text-black flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed bg-[#FF671F] hover:bg-[#FF671F]/90';
    // «Continuar» solo en las que no pasan solas. El peso lleva el suyo («Registrar peso») y
    // la última, «Enviar».
    // Las fotos y las medidas llevan los suyos («Ahora no» y «Continuar»).
    const conContinuar = !['peso', 'fotos', 'medidas'].includes(seccion.clave) && !ultima && !seccion.sola;
    const ahoraNo = (testId) => (
        <button type="button" onClick={() => irA(indice + 1)} data-testid={testId}
            className="w-full rounded-xl py-3 text-sm font-bold uppercase tracking-wider text-foreground border border-border hover:border-foreground/30">
            Ahora no
        </button>
    );

    return (
        <div className="space-y-4" data-testid="cierre-del-dia" data-paso={indice + 1}
            data-seccion={seccion.clave}>
            <div className="space-y-1.5" data-testid="cierre-cabecera">
                {indice > 0 && (
                    <button type="button" onClick={() => irA(indice - 1)} data-testid="cierre-atras"
                        className="text-[13px] text-muted-foreground hover:text-foreground">
                        ‹ Atrás
                    </button>
                )}
                <p className="text-[11px] font-bold uppercase tracking-wider text-brand"
                    data-testid="cierre-titulo-seccion">
                    {seccion.titulo}
                </p>
                <div className="pt-1.5">
                    <BarraDeTramos hechos={indice + 1} de={SECCIONES.length}
                        testid={`cierre-paso-${indice + 1}-de-${SECCIONES.length}`} />
                </div>
            </div>

            {seccion.clave === 'peso' && (
                // EL MISMO «TU PESO» DEL MENSUAL (28-10): la casilla del check-in y «REGISTRAR
                // PESO», que lo guarda en su serie (`CampoDePeso`, el único que la escribe) y
                // pasa a la siguiente. Si toca elegir el peso de referencia semanal, primero eso.
                <div data-testid="cierre-peso">
                    <MensualTuPeso api={api} sinRotulo abrir={abrirPeso}
                        ultimo={hoy?.ultimo_peso || null}
                        onRegistrado={(p, pasar) => { if (pasar) { onPeso?.(); irA(indice + 1); } }} />
                    {/* SIN PESARSE HOY NO SE SIGUE (Enrique, 28-10). Si ya se pesó, «Continuar»:
                        al volver atrás a esta sección, para no tener que registrarlo otra vez. */}
                    {pesadoHoy && (
                        <button type="button" onClick={() => irA(indice + 1)} data-testid="cierre-peso-seguir"
                            className={`mt-3 ${boton}`}>
                            Continuar
                        </button>
                    )}
                </div>
            )}

            {seccion.clave === 'sueno' && (
                <>
                    {tarjeta('horas-sueno', '¿Cuántas horas dormiste anoche?', null, f.horas_sueno != null,
                        <Opciones testId="cierre-horas-sueno-op" columnas={1} value={f.horas_sueno}
                            onChange={v => set('horas_sueno', v)} opciones={HORAS_SUENO_OPCIONES} />)}
                    {tarjeta('descanso', '¿Fue un descanso de calidad?', null, f.descanso != null,
                        <Estrellas testid="cierre-descanso-estrellas" valor={f.descanso}
                            onChange={estrella('descanso')} />)}
                </>
            )}

            {seccion.clave === 'dieta' && (
                <div data-testid="cierre-dieta">
                    <DietaPlanificada tomas={diaDeHoy.tomas} repartoResuelto={diaDeHoy.repartoResuelto}
                        abrir={(t) => navigate(rutaDeLaComida(t))} />
                </div>
            )}

            {seccion.clave === 'entreno' && (
                <>
                    {tarjeta('entreno', '¿Hiciste el entreno que tocaba como tocaba?', null,
                        f.entreno_respuesta != null,
                        <Opciones testId="cierre-entreno-op" columnas={1} value={f.entreno_respuesta}
                            onChange={v => {
                                set('entreno_respuesta', v);
                                // Cambiar de idea limpia lo que colgaba de «entrené».
                                if (!ENTRENO_HECHO.includes(v)) {
                                    set('rendimiento', null);
                                    set('sensaciones_entreno', null);
                                }
                            }}
                            opciones={ENTRENO_OPCIONES} />)}
                    {/* «Solo si marcó una de las tres primeras.» */}
                    {ENTRENO_HECHO.includes(f.entreno_respuesta) && (
                        <>
                            {tarjeta('rendimiento', '¿Y qué tal fue a nivel rendimiento?', null,
                                f.rendimiento != null,
                                <Opciones testId="cierre-rendimiento-op" columnas={1} value={f.rendimiento}
                                    onChange={v => set('rendimiento', v)} opciones={RENDIMIENTO_OPCIONES} />)}
                            {tarjeta('sensaciones-entreno', '¿Buenas sensaciones entrenando?', null,
                                f.sensaciones_entreno != null,
                                <Opciones testId="cierre-sensaciones-entreno-op" columnas={1}
                                    value={f.sensaciones_entreno}
                                    onChange={v => set('sensaciones_entreno', v)}
                                    opciones={SENSACIONES_ENTRENO_OPCIONES} />)}
                        </>
                    )}
                </>
            )}

            {seccion.clave === 'energia' && (
                tarjeta('energia', 'Niveles de energía durante el día',
                    'Fuera del entrenamiento, en tu día a día', f.energy != null,
                    <EstrellasConExtremos testid="cierre-energia-estrellas" valor={f.energy}
                        onChange={estrella('energy')}
                        uno="Bajito, vivo permanentemente cansado"
                        cinco="Genial, con las pilas cargadas todo el día" />)
            )}

            {seccion.clave === 'cardio' && (
                <>
                    {tarjeta('cardio', '¿Cumpliste el cardio pautado?', null, f.cardio != null,
                        <Opciones testId="cierre-cardio-op" pildoras value={f.cardio}
                            onChange={v => set('cardio', v)} opciones={CARDIO_OPCIONES} />)}
                    {tarjeta('actividad', '¿Fuiste lo suficientemente activo?',
                        'Mínimo 8.000 pasos. 10-12 ideal. Más de 12K nivel máster. 10 minutos andando ligero ≈ 1.000 pasos.',
                        f.actividad != null,
                        <Opciones testId="cierre-actividad-op" columnas={1} value={f.actividad}
                            onChange={v => set('actividad', v)} opciones={ACTIVIDAD_OPCIONES} />)}
                </>
            )}

            {seccion.clave === 'acerca_dieta' && (
                <>
                    {tarjeta('cantidad-comida', '¿Qué tal con la cantidad de comida?', null,
                        f.cantidad_comida != null,
                        <Opciones testId="cierre-cantidad-comida-op" columnas={1} value={f.cantidad_comida}
                            onChange={v => set('cantidad_comida', v)} opciones={CANTIDAD_COMIDA_OPCIONES} />)}
                    {tarjeta('hambre', 'Niveles de hambre y ansiedad durante el día', null,
                        f.hunger_anxiety != null,
                        <Estrellas testid="cierre-hambre-estrellas" valor={f.hunger_anxiety}
                            onChange={estrella('hunger_anxiety')}
                            minLabel="1 · Nada" maxLabel="5 · Mucha, y todo el tiempo" />)}
                    {tarjeta('extras', '¿Comiste algo más fuera de la dieta?',
                        'Algo que no registraras como «extra». Si la respuesta es sí, ponlo ahora. Sé sincero, anda, aunque duela.',
                        f.extras_respuesta != null,
                        <Opciones pildoras testId="cierre-extras-op" value={f.extras_respuesta}
                            onChange={v => set('extras_respuesta', v)}
                            opciones={[{ v: 'no', l: 'No' }, { v: 'si', l: 'Sí' }]} />,
                        f.extras_respuesta === 'si' ? (
                            <div data-testid="cierre-extras-campo">
                                <ExtrasDelDia api={api} fecha={fechaDelDia} extras={extras} origen="checkin"
                                    sinRotulo
                                    onAnadido={e => setExtras(prev => [...prev, e])}
                                    onQuitado={id => setExtras(prev => prev.filter(x => x.id !== id))} />
                            </div>
                        ) : null)}
                </>
            )}

            {seccion.clave === 'suplementos' && (
                tarjeta('suplementos', '¿Tomaste la suplementación que tocaba?', null, f.suplementos != null,
                    <Opciones testId="cierre-suplementos-op" columnas={1} value={f.suplementos}
                        onChange={v => set('suplementos', v)} opciones={SUPLES_OPCIONES} />,
                    f.suplementos === 'no_todos' ? (
                        <input value={f.suplementos_detalle}
                            onChange={e => set('suplementos_detalle', e.target.value)}
                            data-testid="cierre-suplementos-detalle"
                            placeholder="¿Cuál y por qué?" className={inputCls} />
                    ) : null)
            )}

            {seccion.clave === 'fotos' && (
                // LAS TRES FOTOS DEL REPORTE (Enrique, 02-11), opcionales: se suben solas al
                // elegirlas y quedan en su serie, la misma que compara el informe del mes.
                <div className="space-y-3" data-testid="cierre-fotos">
                    <TresFotos api={api} token={token} esMensual={false} alCambiarHechas={setFotosHoy} />
                    <button type="button" disabled={fotosHoy < 3} onClick={() => irA(indice + 1)}
                        data-testid="cierre-fotos-seguir" className={boton}>
                        Continuar
                    </button>
                    {ahoraNo('cierre-fotos-ahora-no')}
                </div>
            )}

            {seccion.clave === 'medidas' && (
                // LAS DIEZ MEDIDAS DEL MENSUAL, CON SU VÍDEO (Enrique, 02-11), opcionales.
                <div className="space-y-3" data-testid="cierre-medidas">
                    <TusMedidas medidas={medidas} onCambio={setMedidas}
                        anteriores={tomaAnterior?.measurements} rotuloAnterior=" la última vez" />
                    <button type="button" disabled={!medidasEnteras || guardandoMedidas} onClick={guardarMedidas}
                        data-testid="cierre-medidas-seguir" className={boton}>
                        {guardandoMedidas && <Loader2 className="w-4 h-4 animate-spin" />}
                        Continuar
                    </button>
                    {ahoraNo('cierre-medidas-ahora-no')}
                </div>
            )}

            {seccion.clave === 'notas' && (
                // Las notas son opcionales: no cuentan para «Te queda por contestar».
                <div className="rounded-2xl border border-border bg-card p-4">
                    <span className="text-sm text-foreground font-semibold block">Notas del día</span>
                    <Ayuda>Cosas tuyas que quieras recordar. Lo que escribas aquí irá directo a tu DIARIO.</Ayuda>
                    <textarea rows={3} value={f.notas} onChange={e => set('notas', e.target.value)}
                        data-testid="cierre-notas" placeholder="Escribe aquí"
                        className={inputCls + ' resize-none mt-2'} />
                </div>
            )}

            {aviso.length > 0 && (
                <p className="text-[11px] text-brand" data-testid="cierre-que-falta">
                    Te queda por contestar: {aviso.join(' · ')}
                </p>
            )}

            {conContinuar && (
                <button type="button" onClick={continuar} data-testid="cierre-siguiente" className={boton}>
                    Continuar
                </button>
            )}
            {ultima && (
                <button type="button" disabled={enviando} onClick={enviar} data-testid="cierre-guardar" className={boton}>
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Enviar
                </button>
            )}
        </div>
    );
};

// AQUI VIVIAN LAS FOTOS DE PROGRESO (`PhotoThumb` y `PhotosSection`), la rejilla por
// meses y la subida de la primera foto. FUERA del cierre del dia (punto 17 del doc
// 24-08): «Debajo del Guardar: Anotado. Manana seguimos, Editar lo de hoy, Ver mi
// diario. Y nada mas». Jesus lo senala ademas en el punto 53: «el check-in se ha
// llenado de cosas que no pedi: una galeria entera de fotos de progreso, con la cara».
// Las fotos se ven en Seguimiento -> Evolucion, y se suben con TresFotos, que vive en
// ReportsPage.

const CheckInsPage = () => {
    const { api, pantalla } = useAuth();
    // El cierre del día nuevo, detrás de su interruptor del panel (T4). Con él apagado se
    // queda el check-in diario de siempre, que es lo que hay hoy en producción.
    const cierreNuevo = pantalla('t4_cierre_nuevo');
    const [hoy, setHoy] = useState(null);
    // El día de la dieta de hoy: de él salen la lista de extras ya apuntados y la «Dieta
    // planificada». `listo` evita el parpadeo del formulario.
    const [diaHoy, setDiaHoy] = useState({ dia: null, listo: false });
    const navigate = useNavigate();
    // `?abrir=peso` sigue abriendo la casilla del peso.
    const [parametros] = useSearchParams();
    // EL DÍA QUE SE RELLENA ES SIEMPRE EL DE HOY (25-10). Ya no hay ventana: ni el de ayer
    // por la mañana (`?fecha=` ya no se lee) ni espera hasta las 17:00.
    const elDiaQueSeCierra = todayKey();
    const [checkins, setCheckins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    // Reabrir el cierre de hoy para corregirlo (P75, doc 23-08): con esto encendido el
    // formulario vuelve, precargado con lo guardado, y el guardado sustituye al de antes.
    const [editando, setEditando] = useState(false);
    const enTelefono = useEsTelefono();

    const [daily, setDaily] = useState({ energy: null, hunger_anxiety: null });

    // Historial paginado: se muestran 12, "Cargar más" amplía y pide más al backend si hace falta

    // SOLO LOS CIERRES DEL DÍA (`type=daily`), desde el 6-09-2026.
    //
    // Va con la orden de Jesús de ese día («solo quiero que muestre el historial de los
    // días»): quitado el bloque de «Reportes anteriores», pedir también los reportes dejaba
    // la pantalla mintiendo por dos sitios. En producción hay 1.593 entradas mensuales
    // frente a 5 cierres, así que la lista venía llena de filas que ya no se pintan, y de
    // ahí salían las dos: «Cargar más» aparecía y traía doce entradas sin añadir un solo
    // día, y el «Aún no tienes check-ins» no salía nunca porque la cuenta incluía reportes.
    // Filtrando en el servidor, la lista y lo que se ve son la misma cosa.
    //
    // Los reportes NO se pierden: siguen en la base, los sigue sirviendo este mismo
    // endpoint sin filtro, y el cliente los ve en Seguimiento.
    const fetchAll = useCallback(async () => {
        try {
            const ciRes = await api.get('/checkins?type=daily&limit=30');
            const list = Array.isArray(ciRes.data) ? ciRes.data : [];
            setCheckins(list);
        } catch {
            toast.error('Error al cargar check-ins');
        } finally {
            setLoading(false);
        }
    }, [api]);

    // Lo que le falta hoy: lo decide el servidor (entreno, suplementos, la comida que se
    // dejó y si se ha pasado de macros). Si no llega, el cierre se enseña igual con las
    // preguntas de siempre, que es mejor que no dejarle cerrar el día.
    const fetchHoy = useCallback(async () => {
        try {
            // Con el día del cliente: el cierre pregunta por el día que ÉL está viviendo.
            //
            // Salvo que venga con fecha: es LA VENTANA DE LA MAÑANA (doc «El día», 31-08).
            // Hasta las 15:00 el de ayer sigue abierto, y la fila del Inicio trae aquí con
            // `?fecha=` para que se rellene ese y no el de hoy, que aún no ha pasado.
            const { data } = await api.get(`/checkins/hoy?fecha=${elDiaQueSeCierra}`);
            setHoy(data || null);
        } catch (err) {
            console.error('No se pudo consultar el día de hoy:', err?.response?.data || err);
            setHoy(null);
        }
    }, [api, elDiaQueSeCierra]);


    useEffect(() => { fetchAll(); }, [fetchAll]);
    // Se pide siempre, no solo con el cierre nuevo: de aquí sale el último peso de la
    // casilla de arriba, que está para todos (23-10).
    useEffect(() => { fetchHoy(); }, [fetchHoy]);

    // «?abrir=diario» llevaba a «Mi diario», que se fue el 28-10: los enlaces viejos van al
    // «Histórico de check-ins» de Seguimiento.
    const abrirDiario = parametros.get('abrir') === 'diario';
    useEffect(() => {
        if (abrirDiario) navigate('/dashboard/reports?abrir=checkins', { replace: true });
    }, [abrirDiario, navigate]);

    // La dieta de HOY, con la fecha que dice el servidor (`/checkins/hoy`), que es el
    // único que sabe qué día es en España: aquí no se saca ninguna fecha del navegador.
    // Si el día no se puede traer, el cierre sale largo, que nunca pregunta de menos.
    const fechaHoy = hoy?.fecha;
    // El día que se está cerrando, y si es el de hoy: la cabecera y el «Editar lo de hoy»
    // hablan de ESE día (punto 76). Hasta que el servidor conteste vale el de la URL.
    const diaCerrado = fechaHoy || elDiaQueSeCierra;
    const cierraHoy = diaCerrado === todayKey();
    // LA DIETA DE HOY, para la «Dieta planificada» del check-in (28-10): el día de la dieta,
    // lo mismo que pide Inicio. Si no llega, el bloque no sale: es informativo y no frena nada.
    useEffect(() => {
        if (!cierreNuevo || !fechaHoy) return;
        let cancelado = false;
        api.get(`/diets/${fechaHoy}`)
            .then(r => r.data || null)
            .catch(err => {
                console.error('No se pudo consultar la dieta de hoy:', err?.response?.data || err);
                return null;
            })
            .then(d => { if (!cancelado) setDiaHoy({ dia: d, listo: true }); });
        return () => { cancelado = true; };
    }, [cierreNuevo, api, fechaHoy]);

    const todayDaily = checkins.find(c => c.type === 'daily' && isSameDay(c.created_at));
    // ¿Se está rellenando? Entonces el peso va dentro, en el paso 1 (artefacto, 26-10).
    const rellenando = cierreNuevo && !(hoy?.hecho && !editando);

    // EL PESO YA NO SE ESCRIBE AQUÍ, así que tampoco se valida aquí. Los dos filtros del
    // #48 del 15-08 -- el rango, que rechaza, y el salto de 10 kg, que pregunta -- viajaron
    // enteros con el campo a `components/CampoDePeso.jsx`, con la misma `revisarPeso` y el
    // mismo diálogo de confirmación. Allí el peso con el que se compara sale de la serie
    // (la curva de Evolución), que es mejor referencia que la que había aquí: esto miraba
    // solo los check-in y en producción casi ningún cierre trae peso.

    const submitDaily = async () => {
        if (daily.energy == null || daily.hunger_anxiety == null) {
            return toast.error('Dinos cómo vas de energía y de hambre');
        }
        setSubmitting(true);
        try {
            await api.post('/checkins', { type: 'daily', fecha: elDiaQueSeCierra,
                                          hoy: todayKey(),
                                          hora_cliente: ahoraDeLaApp().getHours(), ...daily });
            toast.success('Check-in diario enviado');
            setDaily({ energy: null, hunger_anxiety: null });
            fetchAll();
        } catch (err) {
            toast.error(mensajeDeError(err, 'No hemos podido guardar tu día. Inténtalo en un momento.'));
        }
        finally { setSubmitting(false); }
    };


    if (loading) {
        return (
            <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1100px] mx-auto">
                <div className="animate-pulse space-y-4">
                    <div className="h-20 bg-muted rounded-2xl" />
                    <div className="h-64 bg-muted rounded-2xl" />
                </div>
            </div>
        );
    }

    // `checkins` ya no pinta ninguna lista: se queda porque de él sale `todayDaily`, que
    // es lo que dice si el día ya está cerrado y enseña el «Anotado. Mañana seguimos».

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1100px] mx-auto space-y-5 animate-fade-in" data-testid="checkins-page">
            {/* LA VUELTA. En el teléfono esta pantalla ya no está en el menú: se llega desde
                la fila de Inicio y desde la puerta «El cierre del día» de Seguimiento (doc
                «El día · parte 1», 30-08), así que tiene que haber una puerta de vuelta a
                mano. En escritorio no hace falta: ahí sigue en la barra lateral.
                Va con `enTelefono` y no con `lg:hidden` a propósito: oculto con CSS el nodo
                sigue en el árbol, y el `space-y-5` del contenedor le da su margen al hermano
                de al lado igualmente. Eran 21 px que aparecían en la vista de escritorio sin
                que nada se viera. */}
            {enTelefono && (
                <button onClick={() => navigate('/dashboard/reports')} data-testid="volver-a-seguimiento"
                    className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
                    <ChevronLeft className="w-4 h-4" /> Seguimiento
                </button>
            )}

            {/* EL PESO, SIEMPRE A MANO (Francisco, 23-10). Vivía dentro del cierre, y el
                cierre no siempre está: con el día ya cerrado salía solo el «Anotado» y entre
                las 15:00 y su hora no hay día abierto. «Hoy toca pesarte» y «Registrar peso»
                de Mi día traen aquí (`?abrir=peso`), así que la casilla tiene que estar
                siempre. Es el mismo `CampoDePeso`, el único que escribe en la serie; se
                guarda por su cuenta y no viaja con el cierre. Y va fuera del interruptor
                y del interruptor del cierre nuevo: quien no cierra el día también se pesa. */}
            {/* Con el check-in abierto, la casilla va en su paso 1 («Tu peso del día»).
                Y CON EL CHECK-IN YA ENVIADO, FUERA DEL RESUMEN (Francisco, 28-10): solo sale si
                viene a pesarse (`?abrir=peso`, de «Hoy toca pesarte» en Mi día y «Apuntar mi
                peso» en Mi semana), para que esos enlaces no lleven a una pantalla sin él. */}
            {hoy && !rellenando && parametros.get('abrir') === 'peso' && (
                <div className="rounded-2xl border border-border bg-card p-4" data-testid="cierre-peso">
                    <CampoDePeso api={api} ultimo={hoy?.ultimo_peso || null}
                        diasAtras={hoy?.peso_dias_atras} onGuardado={fetchHoy}
                        abrir={parametros.get('abrir') === 'peso'}
                        conLosDosNumeros={false} soloHoy />
                </div>
            )}

            <>
            <header className="flex items-center gap-3">
                {/* El check-in nuevo va sin icono, como en el artefacto (26-10). */}
                {!cierreNuevo && (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-brand/10">
                        <Activity className="w-6 h-6 text-brand" />
                    </div>
                )}
                <div>
                    {/* Un solo nombre en los dos tamaños (T4): esto es el cierre del día y
                        se llama como lo que pregunta. «Seguimiento» era el nombre de la
                        pestaña de la que se viene, y en escritorio había DOS pantallas
                        llamadas igual. */}
                    {cierreNuevo ? (
                        <>
                            {/* «CHECK-IN DIARIO» y debajo «Tu resumen del día» (artefacto, 26-10). */}
                            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground leading-none" data-testid="checkins-heading">
                                Check-in diario
                            </h1>
                            <p className="text-sm text-muted-foreground mt-1" data-testid="checkins-subtitulo">
                                {/* El día que no es hoy, por su nombre (punto 76). */}
                                {cierraHoy ? 'Tu resumen del día' : `Tu resumen del ${nombreDelDia(diaCerrado)}`}
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground leading-none" data-testid="checkins-heading">
                                <span className="lg:hidden">¿Cómo vas hoy?</span>
                                <span className="hidden lg:inline">Seguimiento</span>
                            </h1>
                        </>
                    )}
                </div>
            </header>

            {/* El cierre del día */}
            {cierreNuevo ? (
                // Si ya cerró hoy lo dice el servidor, que es el único que sabe qué día es
                // en España: aquí el día se sacaba de la fecha UTC del navegador.
                hoy?.hecho && !editando ? (
                    <Card className="p-4 border-l-4 border-l-emerald-500 flex items-start gap-3" data-testid="checkins-content">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-bold text-foreground">Anotado. Mañana seguimos.</p>
                            {/* Debajo del Guardar, estas dos y nada más (punto 17 del doc
                                24-08). Guardado no es sellado (P75, doc 23-08): el mismo
                                día se puede reabrir, corregir y volver a guardar. */}
                            <button type="button" onClick={() => setEditando(true)} data-testid="cierre-editar"
                                className="mt-1.5 block text-sm text-brand font-semibold hover:underline underline-offset-4">
                                {cierraHoy ? 'Editar lo de hoy' : `Editar lo del ${nombreDelDia(diaCerrado)}`}
                            </button>
                            {/* Y su histórico, donde está lo que acaba de enviar (28-10). */}
                            <button type="button" onClick={() => navigate(`/dashboard/reports?abrir=checkins&dia=${diaCerrado}`)}
                                data-testid="cierre-ver-historico"
                                className="mt-1 block text-sm text-brand font-semibold hover:underline underline-offset-4">
                                Ver histórico
                            </button>
                        </div>
                    </Card>
                ) : hoy?.fecha && !diaHoy.listo ? (
                    // Sin la dieta de hoy todavía no se sabe qué avisar arriba ni qué
                    // extras tiene apuntados: mejor un latido que un formulario que cambia
                    // de tamaño delante del cliente.
                    <div className="h-64 bg-muted rounded-2xl animate-pulse" data-testid="checkins-content" />
                ) : (
                    <div data-testid="checkins-content">
                        <CierreDelDia api={api} hoy={hoy} dia={diaHoy.dia}
                            onPeso={fetchHoy} abrirPeso={parametros.get('abrir') === 'peso'}
                            inicial={editando ? hoy?.checkin : null}
                            /* ENVIADO, A SU HISTÓRICO CON ÉL ABIERTO (Enrique, 28-10). */
                            onGuardado={(dia) => navigate(`/dashboard/reports?abrir=checkins&dia=${dia}`)} />
                    </div>
                )
            ) : todayDaily ? (
                <Card className="p-4 border-l-4 border-l-emerald-500 flex items-start gap-3" data-testid="checkins-content">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold text-foreground">Check-in de hoy hecho</p>
                        <p className="text-sm text-foreground/60 mt-0.5">
                            Energía {todayDaily.energy}/5
                            {todayDaily.hunger_anxiety != null && ` · Hambre ${todayDaily.hunger_anxiety}/5`}
                            {/* La dieta la rellena el sistema con lo registrado, no él. */}
                            {todayDaily.nutrition_followed != null && (todayDaily.nutrition_followed ? ' · Dieta registrada' : ' · Sin dieta registrada')}
                        </p>
                    </div>
                </Card>
            ) : (
                <Card className="overflow-hidden" data-testid="checkins-content">
                    <div className="px-5 pt-5 pb-3 flex items-center gap-2">
                        <Activity className="w-4 h-4 text-brand" />
                        <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Check-in diario · 10 segundos</p>
                    </div>
                    <div className="px-5 pb-5 space-y-5">
                        {/* DOS campos, ni uno más (documento 31-07, partes 6 y 7.2): solo lo
                            que no está en ningún dato. El ánimo salió, y la dieta y el
                            entreno no se preguntan porque ya constan en lo registrado. */}
                        <div>
                            <span className="text-sm text-foreground/70 mb-2 block">Nivel de energía</span>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map(v => {
                                    const active = daily.energy === v;
                                    return (
                                        <button key={v} type="button" onClick={() => setDaily({ ...daily, energy: v })}
                                            data-testid={`daily-energy-${v}`}
                                            className={`flex-1 py-3 rounded-xl border transition-all flex items-center justify-center gap-1 font-bold text-sm ${active ? 'border-brand bg-brand/10 text-brand' : 'border-border bg-muted text-foreground/50 hover:border-white/30'}`}>
                                            <Zap className="w-3.5 h-3.5" />{v}
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Las dos escalas o ninguna: la de hambre decía «1 = nada · 5 =
                                mucha» y esta no decía nada, así que había que adivinar si el 5
                                era mucha energía o poca (Jesús, 11-08). */}
                            <p className="text-[11px] text-foreground/40 mt-1.5">1 = por los suelos · 5 = a tope</p>
                        </div>
                        <div>
                            <span className="text-sm text-foreground/70 mb-2 block">Ansiedad y hambre</span>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map(v => {
                                    const active = daily.hunger_anxiety === v;
                                    return (
                                        <button key={v} type="button" onClick={() => setDaily({ ...daily, hunger_anxiety: v })}
                                            data-testid={`daily-hunger-${v}`}
                                            className={`flex-1 py-3 rounded-xl border transition-all flex items-center justify-center gap-1 font-bold text-sm ${active ? 'border-brand bg-brand/10 text-brand' : 'border-border bg-muted text-foreground/50 hover:border-white/30'}`}>
                                            {v}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-foreground/40 mt-1.5">1 = nada · 5 = mucha</p>
                        </div>
                        {/* AQUÍ ESTABA «¿QUÉ HAS COMIDO HOY?», y se cae (T4/T11 del doc
                            16-08). Lo que se come ya se registra en Nutrición, y el cierre
                            del día pregunta por lo que la app no puede saber. El campo
                            `comido_hoy` se sigue aceptando en el backend: los que ya están
                            escritos no se tiran. */}
                        <button onClick={submitDaily} disabled={submitting}
                            className="w-full bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar check-in
                        </button>
                    </div>
                </Card>
            )}
            </>

            {/* AQUÍ ESTABAN EL CHECK-IN SEMANAL Y EL MENSUAL, y se caen (T11 del doc 16-08).
                Lo que pedían vive ya en otro sitio: el peso, el sueño, el estrés y las notas
                del semanal están en el cierre del día; el peso y las medidas del mensual, en
                el reporte del mes. Eran tres puertas para el mismo dato y ninguna decía cuál
                era la buena.
                El backend los sigue aceptando por compatibilidad (`POST /checkins` con type
                weekly o monthly): los que ya están guardados se siguen leyendo, y salen aquí
                abajo en el historial. Lo que desaparece son los formularios. */}

            {/* LAS FOTOS SE FUERON DE AQUÍ (punto 17). Estaban en un `hidden lg:block`, o
                sea solo en escritorio, y es justo donde Jesús las vio: «debajo del
                "Anotado" hay ahora una galería entera de fotos de progreso, con la cara»
                (punto 53). Se ven en Seguimiento -> Evolución. */}

            {/* AQUÍ IBA «MI DIARIO» (22-10), con la lista de días y el Diario. Se fue el 28-10
                (Enrique): los check-in pasados están en Seguimiento -> «Histórico de
                check-ins». */}
        </div>
    );
};

export default CheckInsPage;
